import { z } from 'zod';
import {
  EVENT_STATUSES,
  GROUND_BLOCKS,
  LEVELS,
  RESERVATION_SOURCES,
  RESERVATION_STATUSES,
  SEAT_KINDS,
} from './enums.ts';

// --- Primitives ---

export const uuid = z.string().uuid();
export const isoDateTime = z.string().datetime({ offset: true });

// Turkish-friendly phone: accepts +90... or 0... formats; normalization happens server-side.
export const phoneSchema = z
  .string()
  .trim()
  .min(7, 'Telefon çok kısa')
  .max(20, 'Telefon çok uzun')
  .regex(/^[+\d\s()-]+$/, 'Telefon yalnızca rakam ve +, -, (, ) içerebilir');

export const emailSchema = z.string().trim().toLowerCase().email('Geçersiz e-posta');

export const fullNameSchema = z
  .string()
  .trim()
  .min(2, 'Ad Soyad zorunlu')
  .max(120, 'Ad Soyad çok uzun');

// --- Enums (re-exported as zod) ---

export const levelEnum = z.enum(LEVELS);
export const seatKindEnum = z.enum(SEAT_KINDS);
export const reservationStatusEnum = z.enum(RESERVATION_STATUSES);
export const reservationSourceEnum = z.enum(RESERVATION_SOURCES);
export const eventStatusEnum = z.enum(EVENT_STATUSES);
export const groundBlockEnum = z.enum(GROUND_BLOCKS);

// --- DTOs (kept minimal in Faz 0; expanded in later phases) ---

export const seatDtoSchema = z.object({
  id: uuid,
  level: levelEnum,
  sectionCode: z.string(),
  rowLabel: z.string(),
  seatNumber: z.number().int().positive(),
  block: groundBlockEnum.nullable(),
  kind: seatKindEnum,
  isSellable: z.boolean(),
  posX: z.number(),
  posY: z.number(),
  label: z.string(),
});
export type SeatDto = z.infer<typeof seatDtoSchema>;

// Admin-side manual reservation customer. Email is optional (the box office
// often has only a phone). The public web flow uses webReservationCustomerSchema
// below instead — it captures the öğrenci (student) + veli (guardian), no email.
export const customerInputSchema = z.object({
  fullName: fullNameSchema,
  email: emailSchema.optional(),
  phone: phoneSchema,
  note: z.string().trim().max(500).optional(),
  kvkkAccepted: z.literal(true, {
    errorMap: () => ({ message: 'KVKK onayı zorunludur' }),
  }),
});
export type CustomerInput = z.infer<typeof customerInputSchema>;

// Public web booking: the veli (guardian) reserves under a davetiye-list
// öğrenci (student) name. No e-mail — notifications go over WhatsApp/phone.
// The chosen student is sent as `studentId` on the reservation body; the
// reservation is stored under the student's name (fullName) with the veli's
// name kept alongside (guardianName).
export const guardianNameSchema = z
  .string()
  .trim()
  .min(2, 'Veli adı zorunlu')
  .max(120, 'Veli adı çok uzun');

export const webReservationCustomerSchema = z.object({
  guardianName: guardianNameSchema,
  phone: phoneSchema,
  email: emailSchema,
  note: z.string().trim().max(500).optional(),
  kvkkAccepted: z.literal(true, {
    errorMap: () => ({ message: 'KVKK onayı zorunludur' }),
  }),
});
export type WebReservationCustomer = z.infer<typeof webReservationCustomerSchema>;

export const createHoldSchema = z.object({
  eventId: uuid,
  seatIds: z.array(uuid).min(1).max(20),
  sessionToken: z.string().min(16).max(128),
});
export type CreateHoldInput = z.infer<typeof createHoldSchema>;

export const createReservationSchema = z.object({
  eventId: uuid,
  seatIds: z.array(uuid).min(1).max(20),
  sessionToken: z.string().min(16).max(128),
  studentId: uuid,
  customer: webReservationCustomerSchema,
});
export type CreateReservationInput = z.infer<typeof createReservationSchema>;

// --- API error envelope (shared between FE and BE) ---

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    fields: z.record(z.string(), z.array(z.string())).optional(),
    conflictSeatIds: z.array(uuid).optional(),
  }),
});
export type ApiError = z.infer<typeof apiErrorSchema>;
